import { isContentAdminRoute } from '~/composables/useContentLockedRoute';
import { isAdminMfaPending } from '~/utils/adminMfaRoute';
import { routeConfigError } from '~/utils/errors/routeConfigError';

export default defineNuxtRouteMiddleware(async (to) => {
  // On the server the Nuxt instance context is lost after the first `await`, so any
  // composable that needs it (e.g. `navigateTo`) must be re-bound via
  // `nuxtApp.runWithContext(...)` once we've awaited — otherwise it throws
  // "A composable that requires access to the Nuxt instance was called outside...".
  const nuxtApp = useNuxtApp();

  const platformAppConfig = usePlatformAppConfig(to.path);

  // Get the authTypes from meta
  const authTypes = to.meta.authType || [AUTH_ROUTE_TYPE_AUTHENTICATED];

  // Check if authTypes exists and is an array
  if (authTypes && !Array.isArray(authTypes)) {
    throw routeConfigError(`Route ${to.path} has invalid authType configuration. Expected array.`);
  }

  const { user } = useAuth();

  const guards = {
    [AUTH_ROUTE_TYPE_NONE]: {
      check: () => true,

      failedRedirect: async () => {},
    },
    [AUTH_ROUTE_TYPE_AUTHENTICATED]: {
      check: () => {
        if (!user.value) {
          return false;
        }
        return platformAppConfig.userHasRequiredRole(user.value);
      },
      failedRedirect: async () => {
        return await navigateTo(clusterRedirectPath(platformAppConfig.unAuthenticatedRedirectRoute), { replace: true });
      },
    },
    [AUTH_ROUTE_TYPE_GUEST]: {
      check: () => {
        if (!user.value) {
          // if not authed, is a guest
          return true;
        }
        // an admin still owing the second factor finishes it on the admin login
        if (isContentAdminRoute(to.path) && isAdminMfaPending(user.value.adminMfaStatus)) {
          return true;
        }
        // if authed, they're a guest if missing the required role
        return !platformAppConfig.userHasRequiredRole(user.value);
      },
      failedRedirect: async () => {
        return await nuxtApp.runWithContext(() => navigateTo(clusterRedirectPath(platformAppConfig.dashboardRedirectRoute), { replace: true }));
      },

    },
  };

  const passesAnyCheck = authTypes.some((type) => {
    const guard = guards[type];
    return guard && guard.check();
  });

  if (!passesAnyCheck && authTypes[0]) {
    const firstGuard = guards[authTypes[0]];
    if (firstGuard) {
      return await nuxtApp.runWithContext(() => firstGuard.failedRedirect());
    }
  }
});
